import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { date, number, object, string } from 'yup';

import { useToast } from 'Component/Alerta';
import BotaoLink from 'Component/BotaoLink';
import Cabecalho from 'Component/Cabecalho';
import Form, { Botao, CaixaDeTexto, Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import LayoutDefault from 'Component/Layout';
import FechamentoDeFolha from 'Interface/FechamentosDeFolha';
import OrdemDeServico from 'Interface/OS/OrdensDeServico';
import { useAppSelector } from 'Store';
import { useAdicionarFechamentoDeFolhaMutation } from 'View/Service/Default/FechamentosDeFolha';
import { useRecuperarOrdensDeServicoQuery } from 'View/Service/Default/OrdensDeServico';
import { useRecuperarVinculosEmpregaticiosQuery } from 'View/Service/Default/VinculosEmpregaticios';

const regras = object({
  vinculo_id: number().required('Obrigatório'),
  inicio: date().required('Obrigatório'),
  termino: date().required('Obrigatório'),
  salario: number().required('Obrigatório'),
  valor: number().nullable().optional(),
  ajuda_de_custo: number().nullable().optional(),
  premio_de_metas_vendedores: number().nullable().optional(),
  premio_de_metas_geral: number().nullable().optional(),
  vale_transporte: number().nullable().optional(),
  adiantamento: number().nullable().optional(),
  farmacia: number().nullable().optional(),
  medico: number().nullable().optional(),
  odonto: number().nullable().optional(),
  consignado: number().nullable().optional(),
  descontos: number().nullable().optional(),
  observacoes: string().nullable().optional(),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const toast = useToast();
  const { usuarioId = 0 } = useParams();
  const [inicio, setInicio] = useState<string>();
  const [termino, setTermino] = useState<string>();
  const [adicionarFolha] = useAdicionarFechamentoDeFolhaMutation();

  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);

  const { data: { vinculos } = {} } = useRecuperarVinculosEmpregaticiosQuery({
    finder: 'listaJson',
    contain: ['Empresas'],
    options: { usuario_id: usuarioId },
  });

  const {
    data: { ordens_de_servico: [ordem] } = {
      ordens_de_servico: [{} as OrdemDeServico],
    },
  } = useRecuperarOrdensDeServicoQuery(
    {
      finder: 'pontuacaoDoTecnicoEntreDatas',
      options: {
        email: usuario?.email,
        inicio: inicio,
        termino: termino,
      },
    },
    { skip: !inicio || !termino }
  );

  const enviar = async (dados: FechamentoDeFolha): Promise<void> => {
    try {
      await adicionarFolha(dados).unwrap();

      toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });

      navegacao(`/usuarios/editar/${usuarioId}`);
    } catch (error) {
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <LayoutDefault>
      <Cabecalho
        titulo="Adicionando arquivo de fechamento de folha"
        descricao="Preencha o formulário e clique em enviar para adicionar o arquivo"
      />
      <Form onSubmit={enviar} regras={regras}>
        <div className="row justify-content-center">
          <div className="col-md-6">
            <div className="bg-white shadow-sm rounded p-4">
              <div className="row">
                <div className="col-md-12">
                  <Selecao
                    name="vinculo_id"
                    rotulo="Vinculo empregatício"
                    opcoes={vinculos}
                    autoSelecao
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="inicio"
                    type="date"
                    rotulo="Data inicial"
                    prefixar={<Icone nome="calendar" />}
                    aoAtualizar={setInicio}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="termino"
                    type="date"
                    rotulo="Data final"
                    prefixar={<Icone nome="calendar" />}
                    aoAtualizar={setTermino}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="salario"
                    rotulo="Salário"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="valor"
                    rotulo="Comissão"
                    prefixar={<Icone nome="currency-dollar" />}
                    forcarValor={ordem.pontuacao_tecnico}
                    desabilitarAoForcar={false}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="ajuda_de_custo"
                    rotulo="Ajuda de custo/combustível"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="premio_de_metas_vendedores"
                    rotulo="Prêmio de metas - Vencedores"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="premio_de_metas_geral"
                    rotulo="Prêmio de metas - Geral"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="vale_transporte"
                    rotulo="Vale transporte"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="adiantamento"
                    rotulo="Adiantamento"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="farmacia"
                    rotulo="Con. farmácia"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="medico"
                    rotulo="Con. Médico"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="odonto"
                    rotulo="Con. Odonto"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="consignado"
                    rotulo="Emprestimo Consignado"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-6">
                  <Campo
                    name="descontos"
                    rotulo="Outros descontos"
                    prefixar={<Icone nome="currency-dollar" />}
                  />
                </div>
                <div className="col-md-12">
                  <CaixaDeTexto name="observacoes" rotulo="Observações" />
                </div>
              </div>

              <div className="mt-5">
                <Botao>Enviar</Botao>
                <BotaoLink
                  tema="light"
                  to={`/usuarios/editar/${usuarioId}`}
                  className="ms-2"
                >
                  Cancelar
                </BotaoLink>
              </div>
            </div>
          </div>
        </div>
      </Form>
    </LayoutDefault>
  );
};

export default Adicionar;
